// ── privacy copy: single source ────────────────────────────────────
// Claims that sound alike and are factually opposite. They must never be merged
// into one sentence, and must never be paraphrased at a call site. If a claim
// changes, it changes here and nowhere else.
//
//   SENT         — what LEAVES the browser (Anthropic, to build a reading)
//   ARRANGEMENT  — what stays. The counterpart to SENT; keep them adjacent.
//   IMAGES_LOCAL — image fragments are not read in this cut (READ_IMAGES=false)
//   CAMERA_LOCAL — camera frames never leave (MediaPipe WASM, local)
//   RECORD       — what the EXPORTED FILE holds. A different axis from SENT:
//                  nothing here crosses the network, but the file is handed to
//                  a researcher, so its contents are a disclosure too.
//   RECORD_IDENTITY — the counterpart to RECORD; keep them adjacent, never
//                  merged. "Holds when you moved things" and "holds no name"
//                  are opposite claims about the same file.
//
// Written against the request this build actually makes, verified by opening
// the intercepted body, not by reading the spec. As of 2-1 + the readingLanguage
// change, the payload is:
//   [{ id, text }] per fragment, sorted by id, plus the lens name, the reading
//   language, and (when one is selected) the model's own previous clue. No
//   coordinates, no resonance, no move counts, no ordering derived from
//   position. Proven by capturing the body across three completely different
//   arrangements: byte-identical.
//
// The language was added when output language moved from inference to input,
// and SENT was not reopened at the time — it went on naming the names and the
// lens only, which states that LESS leaves the browser than does. Caught in 5-3
// by intercepting the body again. That is the whole reason this file says to
// open the body rather than trust the sentence already in it.
//
// If you change what project() or refreshL2() sends, OPEN THE BODY AGAIN and
// rewrite these from what you see. An earlier draft of this file was taken from
// the spec sentence and was wrong.
//
// The same rule applies to RECORD, and it is easier to break: the export grows
// by a field at a time and no request changes, so nothing forces the sentence
// to be reread. Written against the fields attemptRecord() actually emits as of
// 6.1-4, listed rather than summarised so the next person can diff them:
//   record  ts, lens, language, start, phase, edits[], editsTruncated, frozen
//           {frozenAt, field, positions}, fragments[], and per writeLog site
//           status, gen, protocolVersion, modelVersion, languageDetected,
//           languageMismatch, violations
//   frag    id, text, type, resonance, sentToModel, origin, initialPosition,
//           moves, createdAt
// 6.1 added start, phase, edits, editsTruncated, origin, initialPosition,
// moves, createdAt. The record already carried times (ts, frozenAt); what is
// new is within-session granularity — when each fragment was made, and when
// each move happened.

export const PRIVACY = {
  // what leaves the browser
  SENT:
    "To write a reading, your fragment names and the lens and language you chose are sent to Anthropic.",

  // the counterpart. never join this to SENT: the pair IS the claim.
  ARRANGEMENT:
    "Where you placed them stays in your browser. The reading never sees it.",

  // true only while READ_IMAGES is false. flip both together.
  IMAGES_LOCAL:
    "Images you drop on the canvas are not sent in this version. They are placed, not read.",

  // never join this to SENT.
  CAMERA_LOCAL:
    "Camera frames are read in your browser and never leave it. Nothing is recorded, stored, or uploaded.",

  // short form for tight UI slots (button rows, tooltips)
  CAMERA_LOCAL_SHORT:
    "read in your browser, never uploaded",

  // what the exported file holds. not about the network — see the header.
  RECORD:
    "The file holds the words you wrote, where you put them, when each one was made and moved, and the readings that came back.",

  // the counterpart. never join this to RECORD: the pair IS the claim.
  RECORD_IDENTITY:
    "It holds no name, no email and no address. The canvas never asks for one.",
};
